import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  IoAlertCircle,
  IoCheckmarkCircle,
  IoChevronForward,
  IoCloseCircle,
  IoDiceOutline,
  IoEllipseOutline,
  IoPerson,
  IoSparklesOutline,
  IoStar,
  IoSyncOutline,
  IoTimeOutline,
} from 'react-icons/io5'
import heroBackdrop from '../../assets/pixe.png'
import { checkHandle, reserveHandle, claimHandle, purchaseHandle } from '../../api/handles'
import { extractAuthError } from '../../api/auth'
import { useSessionFlags } from '../../state/session'
import { buyPixe, getWalletSummary, getPixePrice, reconcilePurchase } from '../../api/wallet'
import { purchaseAndClaimPremium } from '../../flows/handlePurchaseFlow'

type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'hold'
type CheckResponse = {
  value: string
  normalized: string
  status: Exclude<CheckStatus, 'idle' | 'checking'>
  premium: boolean
  price_usd?: number
  suggestions: string[]
}

const FREE_PAYMENT_REF = 'free-tier'
const TOPUP_CTX_KEY = 'onboarding_handle_topup_ctx_v1'

const premiumExamples = ['@loopboss', '@pixelooppro', '@creatorhub', '@pixedream']

const validChars = (s: string) => /^[a-z0-9](?:[a-z0-9_.-]*[a-z0-9])?$/.test(s)
const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9_.-]/g, '')
const isValidLen = (s: string) => s.length >= 3 && s.length <= 18

function mapStateToStatus(state: string): CheckResponse['status'] {
  switch ((state || '').toLowerCase()) {
    case 'available':
      return 'available'
    case 'reserved':
      return 'hold'
    case 'claimed':
      return 'taken'
    case 'blocked':
    case 'review':
      return 'invalid'
    default:
      return 'invalid'
  }
}

async function apiCheck(value: string): Promise<CheckResponse> {
  const normalized = normalize(value)
  if (!isValidLen(normalized) || !validChars(normalized)) {
    return { value, normalized, status: 'invalid', premium: false, suggestions: [] }
  }
  const res = await checkHandle(value)
  const status = mapStateToStatus(res.state)
  const premium = (res.price ?? 0) > 0
  const priceUsd = res.price ? res.price / 100 : undefined
  return {
    value,
    normalized: res.normalized || normalized,
    status,
    premium,
    price_usd: priceUsd,
    suggestions: quickPicks(normalized),
  }
}

async function apiHold(value: string): Promise<{ reservationId: number; expiresAt: number }> {
  const res = await reserveHandle(value)
  return {
    reservationId: res.reservation_id,
    expiresAt: new Date(res.expires_at).getTime(),
  }
}

async function apiClaim(reservationId: number, provider: 'free' | 'premium' = 'free') {
  const ref = provider === 'free' ? FREE_PAYMENT_REF : `${provider}-${Date.now()}`
  await claimHandle(reservationId, ref)
}

type DebouncedCheckOptions = {
  enabled?: boolean
  onError?: (msg: string, err?: any) => void
}

function useDebouncedCheck(value: string, options?: DebouncedCheckOptions) {
  const enabled = options?.enabled ?? true
  const onError = options?.onError
  const [status, setStatus] = useState<CheckStatus>('idle')
  const [resp, setResp] = useState<CheckResponse | null>(null)

  useEffect(() => {
    if (!enabled) {
      setStatus('idle')
      setResp(null)
      onError?.('')
      return
    }

    const v = normalize(value)
    if (!v) {
      setStatus('idle')
      setResp(null)
      onError?.('')
      return
    }
    if (!isValidLen(v) || !validChars(v)) {
      const invalid: CheckResponse = { value, normalized: v, status: 'invalid', premium: false, suggestions: [] }
      setStatus('invalid')
      setResp(invalid)
      return
    }
    setStatus('checking')
    const id = setTimeout(async () => {
      try {
        const r = await apiCheck(v)
        setResp(r)
        setStatus(r.status)
        onError?.('')
      } catch (err) {
        onError?.(extractAuthError(err as any), err)
        const fallback: CheckResponse = {
          value,
          normalized: v,
          status: 'invalid',
          premium: false,
          suggestions: [],
        }
        setResp(fallback)
        setStatus('invalid')
      }
    }, 300)
    return () => clearTimeout(id)
  }, [value, enabled, onError])

  return { status, resp }
}

function useCountdown(expiresAt: number) {
  const computeLeft = () => Math.max(0, expiresAt - Date.now())
  const [left, setLeft] = useState(computeLeft())
  useEffect(() => {
    if (!expiresAt) {
      setLeft(0)
      return
    }
    setLeft(computeLeft())
    const id = setInterval(() => setLeft(computeLeft()), 250)
    return () => clearInterval(id)
  }, [expiresAt])
  const mm = Math.floor(left / 1000 / 60)
  const ss = Math.floor((left / 1000) % 60)
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n))
  return { left, label: `${mm}:${pad(ss)}` }
}

function rarityScore(handle: string) {
  if (!handle) return 0
  const lenScore = Math.max(0, 18 - handle.length)
  const uniq = new Set(handle).size
  const uniqScore = Math.min(6, Math.max(0, uniq - 4))
  return Math.min(12, lenScore + uniqScore)
}

function quickPicks(seed: string) {
  const base = normalize(seed || 'you')
  const short = base.slice(0, 3) || 'you'
  const pool = [short, `${short}_`, `${short}x`, `real_${short}`, `${base}hq`, `its_${short}`, `${short}ai`]
  return Array.from(new Set(pool)).slice(0, 3)
}

export default function HandlePicker() {
  const navigate = useNavigate()
  const { isAuthenticated } = useSessionFlags()

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const [query, setQuery] = useState('')
  const [checkError, setCheckError] = useState('')

  const [reservationId, setReservationId] = useState<number | null>(null)
  const [holdExpiresAt, setHoldExpiresAt] = useState<number>(0)
  const [selectedHandle, setSelectedHandle] = useState('')
  const [actionError, setActionError] = useState('')
  const [reserving, setReserving] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [topupBusy, setTopupBusy] = useState(false)
  const [showTopup, setShowTopup] = useState(false)
  const [registryDown, setRegistryDown] = useState(false)
  const [walletPixe, setWalletPixe] = useState<number | null>(null)
  const [pixeUsd, setPixeUsd] = useState<number>(1)
  const held = reservationId != null && holdExpiresAt > Date.now()
  const handleCheckError = useCallback((msg: string, err?: any) => {
    setCheckError(msg)
    if (err?.response?.status >= 500) {
      setRegistryDown(true)
    }
  }, [])
  const { status, resp } = useDebouncedCheck(query, { enabled: !registryDown, onError: handleCheckError })

  const { label: holdLabel } = useCountdown(holdExpiresAt)

  useEffect(() => {
    if (!isAuthenticated) return
    ;(async () => {
      try {
        const [{ summary }, px] = await Promise.all([
          getWalletSummary().catch(() => ({ summary: { pixe_balance: '0', fiat_estimate: '0', change_24h_pct: '0', address: '' } })),
          getPixePrice().catch(() => 1),
        ])
        setWalletPixe(Number(summary.pixe_balance))
        setPixeUsd(px || 1)
      } catch {
        // ignore
      }
    })()
  }, [isAuthenticated])

  // Stripe Checkout return handler for onboarding: reconcile credits + retry purchase/claim.
  useEffect(() => {
    if (!isAuthenticated) return

    const url = new URL(window.location.href)
    const qs = url.searchParams
    const checkoutSessionId = (qs.get('checkout_session_id') || '').trim()
    const success = qs.get('success') === '1'
    const canceled = qs.get('canceled') === '1'

    const clearQs = () => {
      qs.delete('checkout_session_id')
      qs.delete('success')
      qs.delete('canceled')
      const next = url.pathname + (qs.toString() ? `?${qs.toString()}` : '')
      window.history.replaceState({}, '', next)
    }

    const loadCtx = (): { reservationId: number; handle: string; expiresAt: number } | null => {
      try {
        const raw = window.localStorage.getItem(TOPUP_CTX_KEY)
        if (!raw) return null
        const ctx = JSON.parse(raw)
        if (!ctx?.reservationId || !ctx?.handle || !ctx?.expiresAt) return null
        return ctx
      } catch {
        return null
      }
    }

    // Restore held reservation across reloads (so we can retry after checkout).
    const ctx = loadCtx()
    if (ctx && (!reservationId || !selectedHandle) && Date.now() < ctx.expiresAt) {
      setReservationId(ctx.reservationId)
      setHoldExpiresAt(ctx.expiresAt)
      setSelectedHandle(ctx.handle)
      setQuery(ctx.handle)
    }

    if (!checkoutSessionId && !success && !canceled) return

    if (canceled) {
      setActionError('Payment canceled. No charges were made.')
      clearQs()
      return
    }

    let cancelled = false
    let tries = 0
    setTopupBusy(true)

    const refreshWalletNow = async () => {
      const d = await getWalletSummary()
      setWalletPixe(Number(d.summary.pixe_balance))
    }

    const retryPurchase = async () => {
      const current = loadCtx()
      if (!current) {
        setActionError('Payment completed but the handle reservation context was missing. Please reserve again.')
        return
      }
      if (Date.now() > current.expiresAt) {
        setActionError('Reservation expired while payment processed. Please reserve the handle again.')
        window.localStorage.removeItem(TOPUP_CTX_KEY)
        return
      }

      const res = await purchaseAndClaimPremium(current.reservationId)
      if (!res.ok) throw new Error('Purchase completed but handle claim could not be completed.')
      window.localStorage.removeItem(TOPUP_CTX_KEY)
      navigate(`/onboarding/ready?handle=${encodeURIComponent(current.handle)}`, { replace: true })
    }

    const poll = async () => {
      if (!checkoutSessionId) {
        clearQs()
        return
      }
      tries += 1
      try {
        const res = await reconcilePurchase(checkoutSessionId)
        if (cancelled) return
        if (res.status === 'ok') {
          await refreshWalletNow()
          try {
            await retryPurchase()
          } catch (err) {
            setActionError(extractAuthError(err as any) || 'Top-up completed, but we could not finish the handle purchase. Please try again.')
          } finally {
            clearQs()
            setTopupBusy(false)
          }
          return
        }
      } catch {
        // ignore reconcile errors; keep polling briefly
      }

      if (tries >= 30 && !cancelled) {
        setActionError('Payment is still processing. Please wait a moment and try again.')
        clearQs()
        setTopupBusy(false)
        return
      }
    }

    void poll()
    const id = window.setInterval(poll, 2000)
    return () => {
      cancelled = true
      window.clearInterval(id)
      setTopupBusy(false)
    }
  }, [isAuthenticated, navigate, reservationId, selectedHandle])

  useEffect(() => {
    if (!held) return
    const timer = setInterval(() => {
      if (holdExpiresAt <= Date.now()) {
        setReservationId(null)
        setHoldExpiresAt(0)
        setSelectedHandle('')
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [held, holdExpiresAt])

  const onSelectSuggestion = async (value: string) => {
    if (registryDown) {
      setActionError('Handle registry is currently unavailable. Please try again shortly.')
      return
    }
    const normalized = normalize(value)
    setQuery(normalized)
    await startHold(normalized)
  }

  const startHold = async (value?: string) => {
    if (registryDown) {
      setActionError('Handle registry is currently unavailable. Please try again shortly.')
      return
    }
    const target = value || resp?.normalized
    if (!target) return
    if (!value && resp && resp.status !== 'available') return
    setReserving(true)
    setActionError('')
    try {
      const hold = await apiHold(target)
      setReservationId(hold.reservationId)
      setHoldExpiresAt(hold.expiresAt)
      setSelectedHandle(target)
    } catch (err) {
      setActionError(extractAuthError(err as any))
    } finally {
      setReserving(false)
    }
  }

  const releaseHold = () => {
    setReservationId(null)
    setHoldExpiresAt(0)
    setSelectedHandle('')
    setActionError('')
  }

  const claimFree = async () => {
    if (registryDown) {
      setActionError('Handle registry is currently unavailable. Please try again shortly.')
      return
    }
    if (!held || reservationId == null || !selectedHandle) return
    setClaiming(true)
    setActionError('')
    try {
      await apiClaim(reservationId, 'free')
      navigate(`/onboarding/ready?handle=${encodeURIComponent(selectedHandle)}`, { replace: true })
    } catch (err: any) {
      const statusCode = err?.response?.status
      if (statusCode === 409) {
        releaseHold()
        setActionError('Someone just grabbed that handle. Try another pick.')
      } else {
        setActionError(extractAuthError(err))
      }
    } finally {
      setClaiming(false)
    }
  }

  const buyPremium = async () => {
    if (registryDown) {
      setActionError('Handle registry is currently unavailable. Please try again shortly.')
      return
    }
    if (!held || reservationId == null || !selectedHandle) return
    setClaiming(true)
    setActionError('')
    try {
      const res = await purchaseAndClaimPremium(reservationId)
      if (!res.ok) {
        const deficit = res.deficit_pixe
        setActionError(deficit ? `Need ${String(deficit)} PIXE to purchase this handle.` : 'Insufficient PIXE balance.')
        setShowTopup(true)
        return
      }
      navigate(`/onboarding/ready?handle=${encodeURIComponent(selectedHandle)}`, { replace: true })
    } catch (err: any) {
      const statusCode = err?.response?.status
      if (statusCode === 402) {
        const deficit = err?.response?.data?.deficit_pixe
        setActionError(deficit ? `Need ${String(deficit)} PIXE to purchase this handle.` : 'Insufficient PIXE balance.')
        setShowTopup(true)
      } else if (statusCode === 409) {
        releaseHold()
        setActionError('Someone just grabbed that handle. Try another pick.')
      } else {
        setActionError(extractAuthError(err))
      }
    } finally {
      setClaiming(false)
    }
  }

  const startCardTopup = async (usd: 10 | 25 | 100) => {
    if (!held || reservationId == null || !selectedHandle) return
    setTopupBusy(true)
    setActionError('Redirecting to card checkout…')
    setShowTopup(true)
    try {
      window.localStorage.setItem(
        TOPUP_CTX_KEY,
        JSON.stringify({ reservationId, handle: selectedHandle, expiresAt: holdExpiresAt }),
      )
      const res = await buyPixe({ amount_usd: usd, return_path: '/onboarding/handlepicker' })
      const url = (res.checkout_url || '').trim()
      if (!url) throw new Error('Unable to start checkout.')
      window.location.assign(url)
    } catch (err) {
      setActionError(extractAuthError(err as any) || 'Unable to start checkout.')
      setTopupBusy(false)
    }
  }

  const isPremium = !!resp?.premium
  const priceLabel = resp?.premium && resp.price_usd != null ? `$${resp.price_usd.toFixed(2)}` : resp ? 'Free' : '—'
  const requiredPixe = resp?.premium && resp.price_usd != null ? resp.price_usd / (pixeUsd || 1) : 0
  const deficitPixe = walletPixe == null ? null : Math.max(0, requiredPixe - walletPixe)
  const canTopupWithCard = held && isPremium && (showTopup || (deficitPixe !== null && deficitPixe > 0))

  const score = rarityScore(resp?.normalized ?? '')
  const rarityPct = Math.round((score / 12) * 100)
  const rarityLabel = rarityPct >= 75 ? 'Rare' : rarityPct >= 45 ? 'Good' : 'Common'
  const showPreview = !!query

  const statusIcon = useMemo(() => {
    if (status === 'checking') return <IoSyncOutline className="h-5 w-5 text-sky-300 animate-spin" />
    if (status === 'available') return <IoCheckmarkCircle className="h-5 w-5 text-emerald-400" />
    if (status === 'taken') return <IoCloseCircle className="h-5 w-5 text-rose-400" />
    if (status === 'invalid') return <IoAlertCircle className="h-5 w-5 text-amber-300" />
    if (held) return <IoTimeOutline className="h-5 w-5 text-yellow-400" />
    return <IoEllipseOutline className="h-4 w-4 text-white/50" />
  }, [status, held])

  const retryRegistry = () => {
    setRegistryDown(false)
    setCheckError('')
    setActionError('')
    setReservationId(null)
    setHoldExpiresAt(0)
    setSelectedHandle('')
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#050505] via-[#08090f] to-black text-white">
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <img src={heroBackdrop} alt="" className="h-full w-full object-cover blur-[140px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,0,122,0.35),_transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,_rgba(0,122,255,0.2),_transparent_55%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-4 pb-20 pt-10 sm:px-6">
        <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-white/60">Handle Claim</span>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-full border border-white/25 px-5 py-2 text-sm font-semibold text-white/80 hover:text-white hover:border-white transition"
          >
            Cancel
          </Link>
        </div>

        <div className="grid gap-10 lg:grid-cols-[1.2fr_0.9fr]">
          <section className="space-y-8">
            <div className="text-center lg:text-left">
              <h1 className="text-3xl font-black md:text-4xl">Claim your handle</h1>
              <p className="mt-3 text-base text-white/75 md:text-lg">
                This is your on-chain identity. Keep it short and memorable. Handles minted here sync across web and mobile.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-white/70">
                On-chain handles
              </div>
            </div>

            {showPreview && (
              <div className="space-y-5 rounded-3xl border border-white/10 bg-white/5 p-6 shadow-[0_25px_60px_rgba(0,0,0,0.35)]">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1d4ed8] shadow-lg shadow-pink-500/30">
                    <IoPerson className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white/70">Your Display Name</p>
                    <p className="text-lg font-black">@{resp?.normalized || normalize(query)}</p>
                  </div>
                  <div className="rounded-full border border-white/15 px-4 py-1 text-sm font-semibold text-white/70">
                    Follow
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-emerald-400 transition-all"
                      style={{ width: `${rarityPct}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-white/80">{rarityLabel}</span>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white/70">Quick picks</p>
                  <button
                    type="button"
                    onClick={() => setQuery(normalize(query) || 'you')}
                    className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/70"
                  >
                    <IoDiceOutline />
                    shuffle
                  </button>
                </div>
                <div className="flex flex-wrap gap-3">
                  {quickPicks(query).map((s) => (
                    <button
                      key={s}
                      onClick={() => onSelectSuggestion(s)}
                      className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
                    >
                      @{s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4 rounded-3xl border border-white/10 bg-white/5 p-6 shadow-[0_25px_60px_rgba(0,0,0,0.35)]">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/20">
                  <IoSparklesOutline className="text-amber-200" />
                </div>
                <p className="text-lg font-bold">Premium handles</p>
              </div>
              <p className="text-sm text-white/70">
                Short or keyword handles mint as scarce on-chain assets you can transfer or sell later.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {premiumExamples.map((example) => (
                  <button
                    key={example}
                    onClick={() => setQuery(example.replace('@', ''))}
                    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left text-sm font-semibold text-white hover:bg-white/10"
                  >
                    <IoSparklesOutline className="text-amber-200" />
                    {example}
                  </button>
                ))}
              </div>
              <p className="text-xs text-white/60">Tap an example to check availability instantly.</p>
            </div>

            <p className="text-sm text-white/60">
              Handles are minted on-chain. Some names are free, others require PIXE to reserve and purchase.
            </p>
          </section>

          <section className="space-y-6">
            {registryDown ? (
              <div className="flex flex-col items-center gap-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 text-center text-amber-100 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
                <p className="text-lg font-semibold">Handle registry is temporarily unavailable.</p>
                <p className="text-sm text-amber-100/90">We hit an unexpected error talking to the handle service. Please try again shortly.</p>
                <button type="button" onClick={retryRegistry} className="rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white hover:bg-white/10">
                  Try again
                </button>
              </div>
            ) : (
              <>
                <div className="relative flex items-center rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-lg font-semibold shadow-2xl shadow-black/30">
                  <span className="mr-1 text-white/60">@</span>
                  <input
                    value={query}
                    onChange={(e) => setQuery(normalize(e.target.value))}
                    placeholder="yourname"
                    autoFocus
                    className="flex-1 bg-transparent text-lg font-semibold tracking-wide text-white outline-none placeholder:text-white/40"
                  />
                  <div className="ml-2 flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-white/70">
                    <span className="hidden md:inline uppercase tracking-[0.2em]">STATUS</span>
                    {statusIcon}
                  </div>
                </div>
                {checkError && <p className="text-sm text-rose-300">{checkError}</p>}

                <div className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
                  {actionError && <p className="text-sm text-rose-300">{actionError}</p>}

                  {resp?.status === 'available' && !held && (
                    <>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded-full bg-emerald-500/20 px-4 py-1 text-sm font-semibold text-emerald-300">
                          Available
                        </span>
                        {isPremium ? (
                          <span className="flex items-center gap-1 rounded-full border border-white/20 px-3 py-1 text-xs uppercase tracking-wide text-white">
                            <IoStar className="text-yellow-300" />
                            {priceLabel}
                          </span>
                        ) : (
                          <span className="rounded-full border border-emerald-300/30 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-200">
                            Free
                          </span>
                        )}
                      </div>
                      <SolidButton
                        onClick={() => startHold()}
                        disabled={reserving}
                        icon={<IoTimeOutline className="h-5 w-5" />}
                      >
                        {reserving ? 'Reserving…' : 'Reserve for 2:00'}
                      </SolidButton>
                    </>
                  )}

                  {held && (
                    <div className="space-y-4 rounded-2xl border border-white/10 bg-black/20 p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm uppercase tracking-[0.3em] text-white/50">Reserved</p>
                          <p className="text-xl font-black">@{selectedHandle || resp?.normalized}</p>
                        </div>
                        <div className="rounded-full border border-white/15 px-4 py-1 text-sm font-mono text-white/80">
                          {holdLabel}
                        </div>
                      </div>
                      {isPremium ? (
                        <GradientButton
                          onClick={buyPremium}
                          disabled={claiming || (deficitPixe !== null && deficitPixe > 0)}
                        >
                          {claiming
                            ? 'Purchasing…'
                            : deficitPixe && deficitPixe > 0
                              ? `Need ${deficitPixe.toFixed(2)} PIXE`
                              : `Purchase for ${requiredPixe.toFixed(2)} PIXE`}
                        </GradientButton>
                      ) : (
                        <GradientButton onClick={claimFree} disabled={claiming}>
                          {claiming ? 'Claiming…' : 'Claim Free Handle'}
                        </GradientButton>
                      )}

                      {canTopupWithCard && (
                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                          <p className="text-sm font-semibold text-white/80">Buy with PIXE tokens</p>
                          <p className="mt-1 text-xs text-white/60">
                            Any leftover PIXE tokens will stay in your wallet. After checkout, we’ll bring you back to Pixeloop so you can continue.
                          </p>
                          <div className="mt-3 grid grid-cols-3 gap-2">
                            <SolidButton onClick={() => startCardTopup(10)} disabled={topupBusy || claiming}>
                              {topupBusy ? '…' : '$10'}
                            </SolidButton>
                            <SolidButton onClick={() => startCardTopup(25)} disabled={topupBusy || claiming}>
                              {topupBusy ? '…' : '$25'}
                            </SolidButton>
                            <SolidButton onClick={() => startCardTopup(100)} disabled={topupBusy || claiming}>
                              {topupBusy ? '…' : '$100'}
                            </SolidButton>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={releaseHold}
                        className="flex items-center gap-2 text-sm font-semibold text-white/60 hover:text-white"
                      >
                        <IoCloseCircle />
                        Release hold
                      </button>
                    </div>
                  )}

                  {resp?.status === 'taken' && (
                    <div>
                      <p className="text-sm font-semibold text-white/80">That one’s taken — try these:</p>
                      <div className="mt-3 flex flex-wrap gap-3">
                        {resp.suggestions.map((s) => (
                          <button
                            key={s}
                            onClick={() => onSelectSuggestion(s)}
                            className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
                          >
                            @{s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {resp?.status === 'invalid' && (
                    <p className="text-sm text-amber-200">
                      Use 3–18 characters: lowercase letters, numbers, underscores, hyphens, or dots.
                    </p>
                  )}
                </div>
              </>
            )}
          </section>
        </div>

      </div>
    </div>
  )
}

function GradientButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-2xl bg-[#1d4ed8] px-6 py-3 text-center text-base font-bold shadow-[0_15px_35px_rgba(0,0,0,0.45)] transition hover:opacity-95 disabled:opacity-60"
    >
      {children}
    </button>
  )
}

function SolidButton({
  children,
  onClick,
  disabled,
  icon,
}: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-6 py-3 text-base font-bold text-white transition hover:bg-white/20 disabled:opacity-60"
    >
      {icon}
      {children}
    </button>
  )
}
